import type { Announcement, AnnouncementInput, AnnouncementSaveResponse } from 'types/announcements';

async function parseJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const message = await res.text().catch(() => '');
    throw new Error(message || `Request failed with ${res.status}`);
  }
  return res.json() as Promise<T>;
}

const jsonBody = (method: string, body: unknown): RequestInit => ({
  method,
  credentials: 'include',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(body),
});

export async function listAnnouncements(): Promise<Announcement[]> {
  const res = await fetch('/api/admin/announcements', { credentials: 'include' });
  const data = await parseJson<{ announcements: Announcement[] }>(res);
  return data.announcements;
}

export async function createAnnouncement(
  input: AnnouncementInput,
): Promise<AnnouncementSaveResponse> {
  return parseJson(await fetch('/api/admin/announcements', jsonBody('POST', input)));
}

export async function updateAnnouncement(
  id: string,
  patch: Partial<AnnouncementInput>,
  opts?: { retranslate?: boolean },
): Promise<AnnouncementSaveResponse> {
  const url = opts?.retranslate
    ? `/api/admin/announcements/${id}?retranslate=true`
    : `/api/admin/announcements/${id}`;
  return parseJson(await fetch(url, jsonBody('PATCH', patch)));
}

export async function retranslateAnnouncement(
  id: string,
): Promise<{
  announcement: Announcement;
  translation: { stale: boolean; failedLanguages: string[] };
}> {
  return parseJson(
    await fetch(`/api/admin/announcements/${id}/translate`, {
      method: 'POST',
      credentials: 'include',
    }),
  );
}

export async function deleteAnnouncement(id: string): Promise<void> {
  const res = await fetch(`/api/admin/announcements/${id}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) {
    const message = await res.text().catch(() => '');
    throw new Error(message || `Request failed with ${res.status}`);
  }
}
